import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Building2, Users, Ticket, Plus, Trash2, Power, Loader2, BarChart3, Shield, Calendar, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function SuperAdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', email: '', phone: '', address: '', slug: '', max_technicians: 5, license_type: 'standard', openai_api_key: '', elevenlabs_api_key: '', twilio_account_sid: '', twilio_auth_token: '', twilio_whatsapp_number: '', sendgrid_api_key: '', sender_email: '', telegram_bot_token: '', telegram_chat_id: '' });
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'admin', company_id: '' });
  const [editCompany, setEditCompany] = useState(null);
  const [editUser, setEditUser] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, compRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/companies`, { headers }),
        axios.get(`${API_URL}/api/admin/users`, { headers })
      ]);
      setStats(statsRes.data);
      setCompanies(compRes.data);
      setAllUsers(usersRes.data);
    } catch (e) {
      console.error('Fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (editCompany) {
        await axios.put(`${API_URL}/api/admin/companies/${editCompany.id}`, companyForm, { headers });
      } else {
        await axios.post(`${API_URL}/api/admin/companies`, companyForm, { headers });
      }
      setShowCompanyModal(false);
      setEditCompany(null);
      setCompanyForm({ name: '', email: '', phone: '', address: '', slug: '', max_technicians: 5, license_type: 'standard', openai_api_key: '', elevenlabs_api_key: '', twilio_account_sid: '', twilio_auth_token: '', twilio_whatsapp_number: '', sendgrid_api_key: '', sender_email: '', telegram_bot_token: '', telegram_chat_id: '' });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setFormLoading(false); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (editUser) {
        // For edit, only update allowed fields
        const updateData = { name: userForm.name, role: userForm.role, company_id: userForm.company_id };
        if (userForm.password) updateData.password = userForm.password;
        await axios.put(`${API_URL}/api/admin/companies/${userForm.company_id}`, {}, { headers }); // placeholder
      }
      await axios.post(`${API_URL}/api/admin/users`, userForm, { headers });
      setShowUserModal(false);
      setEditUser(null);
      setUserForm({ name: '', email: '', password: '', role: 'admin', company_id: '' });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setFormLoading(false); }
  };

  const openEditCompany = async (c) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/companies/${c.id}`, { headers });
      const full = res.data;
      setEditCompany(full);
      setCompanyForm({
        name: full.name || '', email: full.email || '', phone: full.phone || '', address: full.address || '',
        slug: full.slug || '', max_technicians: full.max_technicians || 5, license_type: full.license_type || 'standard',
        openai_api_key: full.openai_api_key || '', elevenlabs_api_key: full.elevenlabs_api_key || '',
        twilio_account_sid: full.twilio_account_sid || '', twilio_auth_token: full.twilio_auth_token || '',
        twilio_whatsapp_number: full.twilio_whatsapp_number || '', sendgrid_api_key: full.sendgrid_api_key || '',
        sender_email: full.sender_email || '', telegram_bot_token: full.telegram_bot_token || '',
        telegram_chat_id: full.telegram_chat_id || ''
      });
      setShowCompanyModal(true);
    } catch (e) {
      alert('Errore nel caricamento');
    }
  };

  const toggleUser = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/toggle-active`, {}, { headers });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { headers });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const toggleCompany = async (companyId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/companies/${companyId}`, { is_active: !currentStatus }, { headers });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const deleteCompany = async (companyId) => {
    if (!window.confirm('Eliminare questa azienda? Tutti gli utenti verranno disattivati.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/companies/${companyId}`, { headers });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  if (user?.role !== 'super_admin') {
    return <div className="text-center py-12"><Shield className="w-12 h-12 mx-auto mb-3 text-red-500" /><p className="text-lg font-medium">Accesso negato</p><p className="text-[#71717A]">Solo il Super Admin può accedere a questa pagina.</p></div>;
  }

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>;

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Pannello Super Admin</h1>
        <p className="text-[#71717A] mt-1">Gestione piattaforma, aziende e licenze</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: 'Aziende', value: stats.total_companies, icon: Building2, color: '#002FA7' },
            { label: 'Attive', value: stats.active_companies, icon: Power, color: '#16A34A' },
            { label: 'Utenti', value: stats.total_users, icon: Users, color: '#D97706' },
            { label: 'Attivi', value: stats.active_users, icon: Users, color: '#16A34A' },
            { label: 'Tecnici', value: stats.total_technicians, icon: Shield, color: '#7C3AED' },
            { label: 'Ticket', value: stats.total_tickets, icon: Ticket, color: '#DC2626' }
          ].map((s, i) => (
            <Card key={i} className="border border-[#E4E4E7] shadow-none">
              <CardContent className="p-4 text-center">
                <s.icon className="w-5 h-5 mx-auto mb-1" style={{ color: s.color }} />
                <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs text-[#71717A]">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="companies">
        <TabsList>
          <TabsTrigger value="companies"><Building2 className="w-4 h-4 mr-2" />Aziende</TabsTrigger>
          <TabsTrigger value="users"><Users className="w-4 h-4 mr-2" />Utenti</TabsTrigger>
        </TabsList>

        {/* Companies Tab */}
        <TabsContent value="companies" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Aziende Clienti</h2>
            <Button className="bg-[#002FA7] text-white" onClick={() => setShowCompanyModal(true)} data-testid="add-company-btn">
              <Plus className="w-4 h-4 mr-2" />Nuova Azienda
            </Button>
          </div>
          <Card className="border border-[#E4E4E7] shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Azienda</TableHead><TableHead>Email Supporto</TableHead><TableHead>Licenza</TableHead><TableHead>API</TableHead><TableHead>Utenti</TableHead><TableHead>Stato</TableHead><TableHead>Azioni</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {companies.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#71717A]">Nessuna azienda. Crea la prima!</TableCell></TableRow>
                  ) : companies.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell><p className="font-medium">{c.name}</p><p className="text-xs text-[#71717A]">{c.email}</p></TableCell>
                      <TableCell><code className="text-xs bg-blue-50 text-[#002FA7] px-2 py-1 rounded">{c.support_email}</code></TableCell>
                      <TableCell><Badge className={c.license_type === 'premium' ? 'bg-purple-100 text-purple-700' : c.license_type === 'trial' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>{c.license_type}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <span title="OpenAI" className={`w-2 h-2 rounded-full ${c.has_openai ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span title="ElevenLabs" className={`w-2 h-2 rounded-full ${c.has_elevenlabs ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span title="Twilio" className={`w-2 h-2 rounded-full ${c.has_twilio ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span title="SendGrid" className={`w-2 h-2 rounded-full ${c.has_sendgrid ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span title="Telegram" className={`w-2 h-2 rounded-full ${c.has_telegram ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        </div>
                      </TableCell>
                      <TableCell>{c.user_count}/{c.max_technicians}</TableCell>
                      <TableCell><Badge className={c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{c.is_active ? 'Attiva' : 'Sospesa'}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => openEditCompany(c)} data-testid={`edit-company-${c.id}`}>
                            Modifica
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => toggleCompany(c.id, c.is_active)} data-testid={`toggle-company-${c.id}`}>
                            <Power className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-500" onClick={() => deleteCompany(c.id)} data-testid={`delete-company-${c.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Tutti gli Utenti</h2>
            <Button className="bg-[#002FA7] text-white" onClick={() => setShowUserModal(true)} data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />Nuovo Utente
            </Button>
          </div>
          <Card className="border border-[#E4E4E7] shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead><TableHead>Azienda</TableHead><TableHead>Stato</TableHead><TableHead>Azioni</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {allUsers.filter(u => u.role !== 'super_admin').map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell><Badge className={u.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}>{u.role === 'admin' ? 'Admin' : 'Tecnico'}</Badge></TableCell>
                      <TableCell className="text-sm">{u.company_name || '-'}</TableCell>
                      <TableCell><Badge className={u.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{u.is_active !== false ? 'Attivo' : 'Bloccato'}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => toggleUser(u.id)} data-testid={`toggle-user-${u.id}`}>
                            <Power className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-500" onClick={() => deleteUser(u.id)} data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Company Modal */}
      <Dialog open={showCompanyModal} onOpenChange={(open) => { setShowCompanyModal(open); if (!open) setEditCompany(null); }}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle>{editCompany ? 'Modifica Azienda' : 'Nuova Azienda'}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateCompany} className="space-y-4">
            <div className="space-y-2"><Label>Nome Azienda</Label><Input value={companyForm.name} onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})} required /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={companyForm.email} onChange={(e) => setCompanyForm({...companyForm, email: e.target.value})} required /></div>
            <div className="space-y-2"><Label>Slug (link supporto)</Label><Input placeholder="es. cdgroup" value={companyForm.slug} onChange={(e) => setCompanyForm({...companyForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})} /><p className="text-xs text-[#71717A]">Link: /support/{companyForm.slug || 'nome-azienda'} | Email: {companyForm.slug || 'slug'}@ikonetsolutions.com</p></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Telefono</Label><Input value={companyForm.phone} onChange={(e) => setCompanyForm({...companyForm, phone: e.target.value})} /></div>
              <div className="space-y-2"><Label>Max Tecnici</Label><Input type="number" value={companyForm.max_technicians} onChange={(e) => setCompanyForm({...companyForm, max_technicians: parseInt(e.target.value)})} /></div>
            </div>
            <div className="space-y-2"><Label>Tipo Licenza</Label>
              <Select value={companyForm.license_type} onValueChange={(v) => setCompanyForm({...companyForm, license_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial (30 giorni)</SelectItem>
                  <SelectItem value="standard">Standard (1 anno)</SelectItem>
                  <SelectItem value="premium">Premium (1 anno)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Indirizzo</Label><Input value={companyForm.address} onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})} /></div>
            
            {/* API Keys Section */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium mb-3">Chiavi API (opzionale — se vuote usa le tue globali)</p>
              <div className="space-y-3">
                <div className="space-y-1"><Label className="text-xs">OpenAI API Key</Label><Input type="password" placeholder="sk-..." value={companyForm.openai_api_key} onChange={(e) => setCompanyForm({...companyForm, openai_api_key: e.target.value})} /></div>
                <div className="space-y-1"><Label className="text-xs">ElevenLabs API Key</Label><Input type="password" placeholder="sk_..." value={companyForm.elevenlabs_api_key} onChange={(e) => setCompanyForm({...companyForm, elevenlabs_api_key: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">Twilio SID</Label><Input type="password" placeholder="AC..." value={companyForm.twilio_account_sid} onChange={(e) => setCompanyForm({...companyForm, twilio_account_sid: e.target.value})} /></div>
                  <div className="space-y-1"><Label className="text-xs">Twilio Token</Label><Input type="password" value={companyForm.twilio_auth_token} onChange={(e) => setCompanyForm({...companyForm, twilio_auth_token: e.target.value})} /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">WhatsApp Number</Label><Input placeholder="whatsapp:+39..." value={companyForm.twilio_whatsapp_number} onChange={(e) => setCompanyForm({...companyForm, twilio_whatsapp_number: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">SendGrid API Key</Label><Input type="password" placeholder="SG..." value={companyForm.sendgrid_api_key} onChange={(e) => setCompanyForm({...companyForm, sendgrid_api_key: e.target.value})} /></div>
                  <div className="space-y-1"><Label className="text-xs">Sender Email</Label><Input placeholder="supporto@..." value={companyForm.sender_email} onChange={(e) => setCompanyForm({...companyForm, sender_email: e.target.value})} /></div>
                </div>
                <div className="border-t pt-3 mt-3">
                  <p className="text-xs font-medium mb-2">Telegram Bot</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Bot Token</Label><Input type="password" placeholder="123456:ABC..." value={companyForm.telegram_bot_token || ''} onChange={(e) => setCompanyForm({...companyForm, telegram_bot_token: e.target.value})} /></div>
                    <div className="space-y-1"><Label className="text-xs">Chat ID (gruppo)</Label><Input placeholder="-100..." value={companyForm.telegram_chat_id || ''} onChange={(e) => setCompanyForm({...companyForm, telegram_chat_id: e.target.value})} /></div>
                  </div>
                  <p className="text-xs text-[#71717A] mt-1">Crea bot su @BotFather. Webhook: /api/telegram/webhook/{companyForm.slug || 'slug'}</p>
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#002FA7] text-white" disabled={formLoading}>
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}{editCompany ? 'Salva Modifiche' : 'Crea Azienda'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="sm:max-w-[450px] bg-white">
          <DialogHeader><DialogTitle>Nuovo Utente</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="space-y-2"><Label>Nome</Label><Input value={userForm.name} onChange={(e) => setUserForm({...userForm, name: e.target.value})} required /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={userForm.email} onChange={(e) => setUserForm({...userForm, email: e.target.value})} required /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" value={userForm.password} onChange={(e) => setUserForm({...userForm, password: e.target.value})} required /></div>
            <div className="space-y-2"><Label>Ruolo</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Amministratore Azienda</SelectItem>
                  <SelectItem value="technician">Tecnico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Azienda</Label>
              <Select value={userForm.company_id} onValueChange={(v) => setUserForm({...userForm, company_id: v})}>
                <SelectTrigger><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-[#002FA7] text-white" disabled={formLoading}>
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Crea Utente
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
